import { useRef } from "react";
import { motion, useAnimationFrame, useMotionValue, useTransform } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";

const CARD_W = 250;
const CARD_H = 350;

function RingItem({ product, i, count, rotation }) {
  const { add, setOpen } = useCart();
  const { format } = useCurrency();
  const step = 360 / count;
  const radius = Math.round(CARD_W / 2 / Math.tan(Math.PI / count) + 110);
  const transform = useTransform(rotation, (r) => `rotateY(${r + i * step}deg) translateZ(${radius}px)`);
  const depth = useTransform(rotation, (r) => Math.cos(((r + i * step) * Math.PI) / 180));
  const opacity = useTransform(depth, [-0.2, 1], [0.1, 1]);
  const zIndex = useTransform(depth, (d) => Math.round(d * 100) + 100);
  const pointerEvents = useTransform(depth, (d) => (d > 0.45 ? "auto" : "none"));

  const handleAdd = () => {
    const v = product.variants?.[0];
    add(product, v?.label || product.sizes?.[0] || null, v?.price ?? product.price);
    setOpen(true);
    toast.success(`${product.name} added to your cart`, {
      action: { label: "View", onClick: () => setOpen(true) },
    });
  };

  return (
    <motion.div
      data-testid={`ring-item-${product.id}`}
      style={{
        transform,
        opacity,
        zIndex,
        pointerEvents,
        width: CARD_W,
        height: CARD_H,
        left: `calc(50% - ${CARD_W / 2}px)`,
        top: `calc(50% - ${CARD_H / 2}px)`,
      }}
      className="absolute backface-hidden bg-coal shadow-[0_30px_60px_-20px_rgba(212,175,55,0.35)] border border-gold/20 overflow-hidden group"
    >
      <Link to={`/product/${product.id}`} data-testid={`ring-link-${product.id}`} className="block h-full w-full" draggable={false}>
        <img src={product.image} alt={product.name} draggable={false} className="h-full w-full object-cover select-none" />
      </Link>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent pointer-events-none" />
      <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/60 transition-colors duration-500 pointer-events-none" />
      {product.tag && (
        <span className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase bg-ivory/90 px-3 py-1.5 text-ink">
          {product.tag}
        </span>
      )}
      <div className="absolute bottom-0 inset-x-0 p-5 text-ivory pointer-events-none [&_button]:pointer-events-auto">
        <p className="text-[9px] tracking-[0.35em] uppercase text-ivory/60">{product.category}</p>
        <div className="flex items-end justify-between gap-3 mt-1.5">
          <h3 className="font-serif text-xl leading-tight">{product.name}</h3>
          <button
            data-testid={`ring-add-${product.id}`}
            onClick={handleAdd}
            aria-label={`Add ${product.name} to cart`}
            className="shrink-0 flex items-center gap-1.5 bg-gold text-ink px-3 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors"
          >
            <Plus size={12} /> {format(product.price)}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function ProductCarousel3D({ products }) {
  const rotation = useMotionValue(0);
  const dragging = useRef(false);
  const velocity = useRef(0);

  useAnimationFrame((_, delta) => {
    if (!dragging.current) {
      rotation.set(rotation.get() + (delta / 1000) * 9 + velocity.current);
      velocity.current *= 0.94;
    }
  });

  if (!products.length) return null;

  return (
    <motion.div
      data-testid="product-ring"
      className="relative h-[480px] md:h-[560px] select-none"
      style={{ perspective: 1700, touchAction: "pan-y" }}
      onPanStart={() => {
        dragging.current = true;
      }}
      onPan={(_, info) => {
        rotation.set(rotation.get() + info.delta.x * 0.35);
        velocity.current = info.delta.x * 0.1;
      }}
      onPanEnd={() => {
        dragging.current = false;
      }}
    >
      <div className="absolute inset-0 preserve-3d" style={{ transform: "rotateX(-7deg)" }}>
        {products.map((p, i) => (
          <RingItem key={p.id} product={p} i={i} count={products.length} rotation={rotation} />
        ))}
      </div>
      <div className="absolute left-1/2 bottom-6 -translate-x-1/2 w-[62%] h-20 rounded-[100%] bg-gold/35 blur-3xl pointer-events-none" />
      <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.4em] uppercase text-ivory/40 whitespace-nowrap">
        Drag to rotate
      </p>
    </motion.div>
  );
}
